/**
 * Keeps the header cart link count in sync with the live cart.
 *
 * The count is server-rendered by Liquid, so on its own it only refreshes on a
 * full page load — adding an item or editing the cart drawer left the header
 * showing a stale value. This listens to the theme cart events and re-renders
 * the localized "item count" label client-side.
 *
 * The three plural forms are passed as data attributes by the header section,
 * so the label stays correctly translated in every locale.
 */
if (!customElements.get('header-cart-count')) {
  class HeaderCartCount extends HTMLElement {
    static SELECTORS = { value: '[data-header-cart-count-value]' };

    static EVENTS = { change: 'cart:change', refresh: 'cart:refresh', pageshow: 'pageshow' };

    /** Placeholder the section leaves in the plural form so we can inject the count. */
    static COUNT_TOKEN = '[count]';

    connectedCallback () {
      this._value = this.querySelector(HeaderCartCount.SELECTORS.value);

      if (!this._value) {
        return;
      }

      this._labels = {
        zero: this.dataset.labelZero || '',
        one: this.dataset.labelOne || '',
        other: this.dataset.labelOther || ''
      };

      this._abort = new AbortController();
      const { signal } = this._abort;

      document.addEventListener(
        HeaderCartCount.EVENTS.change,
        (event) => this._render(event.detail?.cart?.item_count),
        { signal }
      );
      document.addEventListener(HeaderCartCount.EVENTS.refresh, () => this._renderFromServer(), { signal });
      window.addEventListener(HeaderCartCount.EVENTS.pageshow, () => this._renderFromServer(), { signal });
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    _render (count) {
      const itemCount = this._toInt(count, null);

      if (itemCount === null) {
        return;
      }

      this._value.textContent = this._label(itemCount);
    }

    async _renderFromServer () {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);

        if (!response.ok) {
          return;
        }

        this._render((await response.json())['item_count']);
      } catch (error) {
        // Keep the current count rather than replacing it with a wrong one.
      }
    }

    _label (count) {
      if (count === 0) {
        return this._labels.zero;
      }

      if (count === 1) {
        return this._labels.one;
      }

      return this._labels.other.replace(HeaderCartCount.COUNT_TOKEN, count);
    }

    _toInt (value, fallback) {
      const parsed = parseInt(value, 10);

      return Number.isNaN(parsed) ? fallback : parsed;
    }
  }

  customElements.define('header-cart-count', HeaderCartCount);
}
